<template>
  <div class="container-fluid" id="assumptions">
    <div class="row wrapper">
      <div class="col-12 col-md-4 side-banner">
        <div class="header">
          <div id="steps" class="steps" tabindex="0" role="document">
            &nbsp;
          </div>
					<div class="company-logo"></div>
          <h1 tabindex="0">Disclaimer & Assumptions</h1>
				<div class="hero-image" alt="Disclaimer & Assumptions"></div>
        </div>
      </div>
      <div class="col-12 col-md-8 main-banner">
        <div class="main assumptions-field">

			<b-tabs content-class="mt-3" justified>
				<b-tab title="Disclaimer" :active="activeTab === 'disclaimer'">
					<div class="disclaimer-content disclaimer-text" v-html="pageContent.pages.disclaimerCopy"></div>
					<div class="ver text-end pe-2 py-1">
            <small>v{{ appVersion }}r{{ this.revision }}</small>
          </div>
				</b-tab>
				<b-tab title="Edit assumptions" :active="activeTab === 'assumptions'">

					<p>
					The following assumptions have been made when calculating your estimated insurance needs. You can change the values of these assumptions below.
					</p>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="wageInflation"
								fieldName="wageInflation"
								storeType="editAssumptions"
								fieldLabel="Wage inflation"
                customClass="percentage"
								tooltip="Determines how much TPD cover you need to supplement your future income needs."
								:customBlurFunction="triggerHeightCheck"
                :fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.inflation.min"
								:fieldMax="config.fields.inflation.max"
								:errorMessage="pageContent.pages.assumptions.wageInflationError"
								@keydown.native.enter.prevent="$refs.priceInflation.$children[1].$el.focus()">
							</CustomField>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="priceInflation"
								fieldName="priceInflation"
								storeType="editAssumptions"
								fieldLabel="Price inflation"
                customClass="percentage"
								tooltip="Influences your potential future expenses."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.inflation.min"
								:fieldMax="config.fields.inflation.max"
								:errorMessage="pageContent.pages.assumptions.priceInflationError"
								@keydown.native.enter.prevent="$refs.retirementAge.$children[1].$el.focus()">
							</CustomField>
						</div>
					</div>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="retirementAge"
								fieldName="retirementAge"
								storeType="editAssumptions"
								fieldLabel="Retirement age"
								tooltip="Considers income and living expenses for you and your beneficiaries until your retirement age."
								:fieldOptions="formatOptions.age"
								:fieldMin="config.fields.retirementAge.min"
								:fieldMax="config.fields.retirementAge.max"
								:customBlurFunction="updateRetirementAgeRelatedFieldValues"
								:errorMessage="pageContent.pages.assumptions.retirementAgeError"
								@keydown.native.enter.prevent="$refs.lifeInsuranceInvestmentReturn.$children[1].$el.focus()">
							</CustomField>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="lifeInsuranceInvestmentReturn"
								fieldName="lifeInsuranceInvestmentReturn"
								storeType="editAssumptions"
								fieldLabel="Discount rate"
                customClass="percentage"
								tooltip="Applies present values to future cashflows, such as expenses."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.investmentReturn.min"
								:fieldMax="config.fields.investmentReturn.max"
								:errorMessage="pageContent.pages.assumptions.investmentReturnError"
								@keydown.native.enter.prevent="$refs.tpdRepayDebtsLiabilities.focus()">
							</CustomField>
						</div>
					</div>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<label for="" tabindex="0">TPD to include debt &amp; liabilities
                <b-button id="tooltip-assumptions-tpd-debt" variant="tooltip" aria-label="tooltip tpd include debt and liabilities"><i class="icon-help"></i></b-button>
                <b-tooltip target="tooltip-assumptions-tpd-debt" placement="top">
                  If you select yes, your TPD cover will include your debt and liability on top of ongoing expenses.
                </b-tooltip>
              </label>
							<select ref="tpdRepayDebtsLiabilities" class="form-control" aria-label="Default select example"
								v-model="data.editAssumptions.tpdRepayDebtsLiabilities"
								@keydown.enter.prevent="$refs.tpdFutureIncomeLevel.$children[1].$el.focus()">
								<option selected disabled value="">Select option</option>
								<option :value="true">Yes</option>
								<option :value="false">No</option>
							</select>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="tpdFutureIncomeLevel"
								fieldName="tpdFutureIncomeLevel"
								storeType="editAssumptions"
								fieldLabel="TPD income replacement %"
                customClass="percentage"
								tooltip="Reflects the combination of income protection and TPD replacing your future income. The default of 100% assumes your current income is needed to cover your future living expenses and debts."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.tpdFutureIncomeLevel.min"
								:fieldMax="config.fields.tpdFutureIncomeLevel.max"
								:errorMessage="pageContent.pages.assumptions.tpdFutureIncomeError"
								@keydown.native.enter.prevent="$refs.incomeProtectionSuperGuarantee.$children[1].$el.focus()">
							</CustomField>
						</div>
					</div>

					<!-- <div class="row">
						<div class="col">
							<label for="">TPD age to cease allowance for future income</label>
							<b-button id="tooltip-assumptions-9" variant="tooltip"><i class="icon-help"></i></b-button>
							<b-tooltip target="tooltip-assumptions-9" placement="top">
								Hello <strong>World!</strong>
							</b-tooltip>
							<br>
							<input class="form-control" name='tpdCeaseAllowanceForFutureIncomeAtAge' id='tpdCeaseAllowanceForFutureIncomeAtAge' v-model.number="tpdCeaseAllowanceForFutureIncomeAtAge" />
						</div>
					</div> -->

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="incomeProtectionSuperGuarantee"
								fieldName="incomeProtectionSuperGuarantee"
								storeType="editAssumptions"
								fieldLabel="Income Protection - super contribution rate"
                customClass="percentage"
								:tooltip="sgTooltip"
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.sgRate.min"
								:fieldMax="config.fields.sgRate.max"
								:errorMessage="pageContent.pages.assumptions.sgContributionError"
								@keydown.native.enter.prevent="$refs.incomeProtectionPercentage.$children[1].$el.focus()">
							</CustomField>
						</div>
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								ref="incomeProtectionPercentage"
								fieldName="incomeProtectionPercentage"
								storeType="editAssumptions"
								fieldLabel="Income protection - income replacement ratio %"
                customClass="percentage"
								:tooltip="pageContent.pages.assumptions.ipTooltipCopy"
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.percentage"
								:fieldMin="config.fields.incomeProtectionPercentage.min"
								:fieldMax="config.fields.incomeProtectionPercentage.max"
								:errorMessage="pageContent.pages.assumptions.ipPercentageError">
							</CustomField>
						</div>
					</div>

					<div class="row">
						<div class="col-12 col-sm-6 col-md-12 col-lg-6">
							<CustomField
								:readonly="true"
								fieldName="lifeInsurancePaymentPeriod"
								storeType="editAssumptions"
								fieldLabel="Payment period of rent and expenses covered until age"
								tooltip="This considers rent and/or living expenses for your beneficiaries until your elected retirement age."
                :customBlurFunction="triggerHeightCheck"
								:fieldOptions="formatOptions.years"
								:fieldMin="config.fields.lifeInsurancePaymentPeriod.min"
								:fieldMax="config.fields.lifeInsurancePaymentPeriod.max">
							</CustomField>
						</div>
					</div>
					<button class="reset-assumptions-button p-0" v-on:click="resetAssumptions"><i class="icon-undo" tabindex="0"></i>Reset assumptions</button>

				</b-tab>

			</b-tabs>

			<div class="nav-button row">
				<button class="back-button col-auto" @click="$router.go(-1)" tabindex="0" role="button">Back</button>
			</div>

        </div>
      </div>

    </div>
		<CustomFooter></CustomFooter>
  </div>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapGetters } from 'vuex';
import CustomField from '../components/CustomField';
import CustomFooter from '../components/CustomFooter';
import { atoVars } from 'insurance-needs-engine';

export default {
  name: 'Assumptions',
  props: ['activeTab'],
  components: { CustomField, CustomFooter },
	computed: {
    ...mapFields(["data", "appVersion", "revision"]),
		...mapGetters(["config", "formatOptions", "pageContent"]),
		sgTooltip() {
			const sgRate = atoVars.calculationVariables.percentSuperannuationContribution;
			return "We applied the minimum legal super contribution rate of "+sgRate+"%. If your employer pays more, adjust the rate accordingly. To confirm your rate, check your payslip or contact your employer."
		}
  },
	methods: {
		resetAssumptions: function() {
			// console.log('reset to defaults');
			this.$store.commit("setDefaultAssumptions");
		},
		updateRetirementAgeRelatedFieldValues: function() {
			this.$store.commit("setLifeInsurancePaymentPeriod");
			this.$store.commit("settpdCeaseAllowanceForFutureIncomeAtAge");
      this.triggerHeightCheck();
		},
    triggerHeightCheck: function() {
      // Add slight delay otherwise incorrect height is read
      var rf = this;
      setTimeout(function() {
        rf.$refreshIframeHeight();
      }, 300);
    }
	},
	mounted() {
		this.$store.commit("setLifeInsurancePaymentPeriod");
		this.$store.commit("settpdCeaseAllowanceForFutureIncomeAtAge");
		this.triggerHeightCheck();
    this.$scrollIframeTop();
    window.addEventListener("resize", this.$refreshIframeHeight);
	},
  destroyed() {
    window.removeEventListener("resize", this.$refreshIframeHeight);
  },
	beforeDestroy() {
    // send analytics - update assumptions
		this.$gtag.event('assumptions', { event_category: 'update-assumptions', event_label: JSON.stringify(this.data.editAssumptions) });
  },
}
</script>
